import React, { useState } from 'react';
import { Badge, BlockStack, Button, InlineStack, Select, Text, TextField } from '@shopify/polaris';
import { CertaintyPill } from './CertaintyPill';
import MultiValueCell from './MultiValueCell';
import { keyText } from './facetUtils';

const NEW = '__new';
const isEdited = (f) => (f.sources || []).length === 1 && f.sources[0] === 'edit';

const Chip = ({ s }) => <Badge tone={String(s).startsWith('vendor_page') || s === 'edit' ? 'success' : undefined}>{String(s)}</Badge>;

const ROW = { display: 'grid', gridTemplateColumns: 'minmax(150px, 190px) minmax(220px, 1fr) 56px minmax(120px, 200px)', gap: 12, alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid var(--p-color-border-secondary, #e1e3e5)' };

// Free-text facets (colour, model year…) save when the field loses focus with a changed, non-empty value.
function FreeText({ label, value, disabled, onCommit }) {
  const [v, setV] = useState(value);
  React.useEffect(() => setV(value), [value]);
  const commit = () => { if (v.trim() && v !== value) onCommit(v.trim()); };
  return <TextField label={label} labelHidden value={v} disabled={disabled} autoComplete="off" placeholder="—" onChange={setV}
    onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />;
}

// One facet row of a group: label with its key, the control, certainty, source chips.
// meta = the dictionary entry (fallback for values); fm = facet_meta from the product payload (allowed list, multi, source).
// A conflict shows both candidates with their sources and a "Use this" button on each.
export default function FacetRow({ id, meta, fm, f, current, onEdit, onAddNew, busy }) {
  const edited = isEdited(f);
  const info = fm || {};
  const label = info.label || meta.label || id;
  const multi = !!info.multi;
  const allowed = info.allowed || (meta.values || []).filter((v) => v.status === 'allowed').map((v) => v.value);
  const lab = (
    <div>
      <Text as="span">{label}</Text>
      <Text as="p" variant="bodyXs" tone="subdued">{keyText(id, info)}{multi ? ' · list' : ''}</Text>
    </div>
  );

  if (f.conflict && !edited && !multi) {
    return (
      <div style={ROW}>
        {lab}
        <BlockStack gap="100">
          {(f.conflicting || []).map((c) => (
            <InlineStack key={c.value} gap="200" blockAlign="center" wrap>
              <Text as="span" fontWeight="semibold">{c.value}</Text>
              {(c.sources || []).map((s) => <Chip key={s} s={s} />)}
              <Button size="micro" disabled={busy} onClick={() => onEdit(id, c.value)}>Use this</Button>
            </InlineStack>
          ))}
        </BlockStack>
        <CertaintyPill value={0} />
        <Text as="span" variant="bodySm" tone="subdued">Sources disagree — pick one.</Text>
      </div>
    );
  }

  const proposed = Array.isArray(f.value) ? f.value.join(', ') : (f.value || '');
  const norm = (t) => String(t).split(',').map((x) => x.trim().toLowerCase()).filter(Boolean).sort().join('|');
  const differs = !!current && norm(current) !== norm(proposed);
  let input;
  if (multi) {
    const values = Array.isArray(f.value) ? f.value : (f.value ? [f.value] : []);
    input = <MultiValueCell label={label} values={values} details={f.values} allowed={allowed} disabled={busy} onChange={(list) => onEdit(id, list)} />;
  } else {
    const val = f.value ?? '';
    const known = allowed.length || info.kind === 'choices' || info.kind === 'metaobject' || info.kind === 'vendor' || meta.kind === 'choices';
    input = known ? (
      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <Select label={label} labelHidden value={val} disabled={busy}
          options={[{ value: '', label: '— none —' }, ...(val && !allowed.includes(val) ? [{ value: val, label: `${val} (pending)` }] : []),
            ...allowed.map((v) => ({ value: v, label: v })), ...(info.source === 'taxonomy' ? [] : [{ value: NEW, label: 'Add new value…' }])]}
          onChange={(v) => (v === NEW ? onAddNew(id, label) : v !== val && onEdit(id, v))} />
        {f.pending && !edited && <Badge tone="attention">pending</Badge>}
      </InlineStack>
    ) : <FreeText label={label} value={val} disabled={busy} onCommit={(v) => onEdit(id, v)} />;
  }
  const none = multi ? !(Array.isArray(f.value) && f.value.length) : !f.value;
  const sources = edited ? ['edit'] : f.sources || [];
  return (
    <div style={ROW}>
      {lab}
      <div>
        {input}
        {differs && <Text as="p" variant="bodyXs" tone="subdued">Shopify now: {current}</Text>}
      </div>
      {none && !edited ? <Text as="span" tone="subdued">—</Text> : <CertaintyPill value={edited ? 100 : f.certainty} />}
      <BlockStack gap="050">
        <InlineStack gap="100" wrap>
          {sources.length ? sources.map((s) => <Chip key={s} s={s} />) : <Text as="span" variant="bodySm" tone="subdued"><em>no evidence</em></Text>}
          {edited && <Badge tone="success">edited</Badge>}
        </InlineStack>
        {f.reason ? <Text as="span" variant="bodySm" tone="subdued">{f.reason}</Text> : null}
      </BlockStack>
    </div>
  );
}
